/**
 * relevant / calculate / constraint evaluation.
 *
 * `packages/audit-rules` has landed (see packages/audit-rules/src/eval.ts) — this module
 * is now a thin adapter over its `resolve`/`evalExpr`/`evalRelevant`/`checkConstraint`,
 * not a second copy. That matters for R5/ADR-8: relevant/calculate must evaluate
 * identically on client and server, and the only way to guarantee that is one
 * implementation, not two hand-kept-in-sync ones.
 *
 * The adapter exists only because the shared package's `evalExpr`/`evalRelevant` argument
 * order differs slightly from what this renderer was originally written against
 * (`expr` first there vs. `answers` first here), and its `Choices` type is nested one
 * level deeper (`choices[formKey][listName]`) than the single-form index the renderer
 * builds from a submission's `formSchemaSnapshot`. `resolve` and `checkConstraint` are
 * re-exported unchanged.
 */
import {
  resolve as sharedResolve,
  evalExpr as sharedEvalExpr,
  evalRelevant as sharedEvalRelevant,
  checkConstraint as sharedCheckConstraint,
} from '@farmer/audit-rules';

export { choiceAllowed, legacyChoiceFilterVar, listOfQuestions } from '@farmer/audit-rules';

export const resolve = (answers, name, prefix) => sharedResolve(answers, name, prefix);

/**
 * @param {Record<string,string>} answers
 * @param {string} expr
 * @param {string} prefix
 * @param {number} idx
 * @param {Record<string, Array<Record<string,string>>>} choicesByList - list name -> rows,
 *   for the single form currently being rendered.
 * @param {string} formKey - required only to satisfy @farmer/audit-rules' nested Choices shape.
 * @param {{listOf?: (q: string) => string|undefined, today?: () => string}} [opts] - only
 *   expressions outside the legacy pattern set read these (`jr:choice-name`, `today()`).
 */
export function evalExpr(answers, expr, prefix, idx, choicesByList, formKey, opts) {
  const choices = formKey && choicesByList ? { [formKey]: choicesByList } : undefined;
  return sharedEvalExpr(expr, answers, prefix, idx, formKey, choices, opts);
}

/** `opts` như evalExpr, thêm `lists` (list name → rows) cho `choice-value`/`jr:choice-name`. */
export function evalRelevant(answers, expr, prefix, opts) {
  return sharedEvalRelevant(expr, answers, prefix, opts);
}

export const checkConstraint = sharedCheckConstraint;

/**
 * Extract the hard max from a constraint expression like ". <= 3000", used to set the
 * HTML `max` attribute on numeric inputs. UI-only concern (not audit truth), so it stays
 * local rather than living in @farmer/audit-rules.
 */
export function constraintMax(expr) {
  const e = String(expr || '');
  const m = e.replace(/string-length\(\.\)[^)]*/g, '').match(/\.\s*(<=|<)\s*'?(-?[\d.]+)'?/);
  return m ? m[2] : '';
}
